import { Component, Input, OnInit, Pipe, PipeTransform, TemplateRef, inject } from '@angular/core';
import { Credential, Customer, ImporterAccount } from '../../model/models';
import {take, Observable, Subject, takeUntil, EMPTY, delay, expand, switchMap, map} from 'rxjs';
import { CustomersService } from '../../services/customers/customers.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { Buffer } from 'buffer';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { DataSourceComponent } from "../data-sources/data-source/data-source.component";
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { SocialAuthService } from '@abacritt/angularx-social-login';
import { environment } from '../../../environments/environment';

type SettingsControl = 'text' | 'number' | 'date' | 'boolean' | 'enum' | 'list' | 'object'

export interface SettingsField {
  key: string
  label: string
  control: SettingsControl
  required: boolean
  superUserOnly: boolean
  enumOptions: string[]
  children: SettingsField[]
}

@Pipe({
  name: 'verificationStatus',
  standalone: true
})
export class VerificationStatusPipe implements PipeTransform {
  repeat$$ = new Subject<void>();
  customersService = inject(CustomersService);
  notification = inject(NzNotificationService);

  transform(accountId: string, customerId: string, _resetKey?: number): Observable<any> {
    return new Observable((observer) => {
      this.repeat$$ = new Subject<void>();
      let aid = Buffer.from(accountId).toString('base64');
      let cid = Buffer.from(customerId).toString('base64');
      if(!aid && !cid) { this.repeat$$.complete(); observer.complete() }
      this.customersService.credentialsVerificationStatus(cid, aid).pipe(
        expand((res:any) => {
          if(res == 'unverified') {
            return this.customersService.credentialsVerificationStatus(cid, aid).pipe(delay(60000))
          }
          this.repeat$$.complete()
          return EMPTY
        }),
        takeUntil(this.repeat$$)
      ).subscribe({
        next: (res: any) => {
          observer.next(res)
        },
        error: (err: any) => {
          this.notification.error(
            'Error:',
            err.error
          )
        },
      })
    })
  }
}

@Component({
  selector: 'importer-accounts',
  standalone: true,
  imports: [CommonModule, FormsModule, NzButtonModule, NzFormModule, NzModalModule, NzSelectModule, NzInputModule, NzDividerModule,
    NzIconModule, NzCardModule, NzSwitchModule, VerificationStatusPipe, NzSkeletonModule, NzEmptyModule, NzTypographyModule,
    NzToolTipModule, NzTabsModule, DataSourceComponent, NzRadioModule, NzSpinModule],
  templateUrl: './importer-accounts.component.html',
  styleUrl: './importer-accounts.component.scss'
})

export class ImporterAccountComponent implements OnInit {
  @Input() customer!: Customer
  customersService = inject(CustomersService);
  modal = inject(NzModalService);
  notification = inject(NzNotificationService);
  authService = inject(SocialAuthService);
  superusers: string[] = JSON.parse(environment.adminToolSuperusers);
  isSuperUser = false;
  existingCredential: ImporterAccount | undefined
  existingGrpAccounts = new Array<any>()
  grpCredentialOption: 'existing' | 'new' = 'new'
  selectedGrpCredentialsId: string | undefined
  availableSystemInfoList = new Array()
  importerAccount: any | undefined
  accounts = new Array<any>()
  configurationRequests: any = []
  credentials = new Array<Credential>()
  supplierSystem: any | undefined
  accountCRUD = false
  accLoading = true
  configurationRequestsLoading = false;
  locations: any;
  error: any | undefined
  deleteTextValue = ""
  editingAccount: ImporterAccount | undefined
  editCredentials: Record<string, string> = {}
  verificationKeys: Record<string, number> = {}
  editingSupplierSystem: any | undefined
  editGrpCredentialOption: 'existing' | 'new' = 'new'
  editSelectedGrpCredentialsId: string | undefined
  editExistingGrpAccounts = new Array<any>()
  editExistingCredential: ImporterAccount | undefined
  rotatingAccountId: string | undefined
  updatingRotateAccountId: string | undefined
  updatingEnabledAccountId: string | undefined
  editingCredentialsAccount: ImporterAccount | undefined
  editingCredentialsSupplierSystem: any | undefined
  editingCredentialsGrpOption: 'existing' | 'new' = 'new'
  editingCredentialsSelectedGrpId: string | undefined
  editingCredentialsExistingGrpAccounts = new Array<any>()
  editingCredentialsData: Record<string, string> = {}
  createSettingsFields: SettingsField[] = []
  editSettingsFields: SettingsField[] = []

  trackBySettingsField = (_: number, field: SettingsField) => field.key

  private readonly secretFieldLabels: Record<string, string> = {
    username: 'Username',
    password: 'Password',
    otp_secret_key: 'OTP secret',
  }

  secretFieldLabel(field: string): string {
    return this.secretFieldLabels[field] ?? this.humanizeSettingsKey(field)
  }

  ngOnInit(): void {
    this.getAvailableSupplierSystem(this.customer.pk)
    this.getImporterAccounts(this.customer.pk)
    this.getConfigurationRequests(this.customer.pk)
    this.authService.authState.subscribe((user) => {
      const email = user?.email?.toLowerCase() ?? ''
      this.isSuperUser = this.superusers.map(e => e.toLowerCase()).includes(email)
    })
  }

  selectSupplierSystem(event: any) {
    this.importerAccount = {} as ImporterAccount
    this.importerAccount.supplier_partner_segment = this.supplierSystem.supplier_partner_segment
    this.importerAccount.supplier_system_partner_id = this.supplierSystem.supplier_system_partner_id
    this.importerAccount.supplier_system_auth_id = this.supplierSystem.supplier_system_auth_id
    this.importerAccount.display_name = ''
    this.importerAccount.website = ''
    this.importerAccount.note = ''
    this.importerAccount.rotate = false
    this.importerAccount.settings = {}
    this.createSettingsFields = this.buildSettingsFields(this.supplierSystem?.settings_schema)
    this.initSettingsValues(this.createSettingsFields, this.importerAccount.settings)
    this.grpCredentialOption = 'new'
    this.selectedGrpCredentialsId = undefined

    if (this.supplierSystem.supplier_system_auth_id === 'grp') {
      this.existingGrpAccounts = this.accounts.filter(elem => {
        const systemInfo = this.availableSystemInfoList.find(
          s => s.supplier_system_partner_id === elem.supplier_system_partner_id
        )
        return systemInfo?.supplier_system_auth_id === 'grp'
      })
      this.existingCredential = this.existingGrpAccounts.length > 0 ? this.existingGrpAccounts[0] : undefined
    } else {
      this.existingGrpAccounts = []
      this.existingCredential = undefined
    }

    this.importerAccount.secrets = {}
  }

  onGrpOptionChange(option: 'existing' | 'new') {
    this.grpCredentialOption = option
    this.selectedGrpCredentialsId = undefined
    if (this.importerAccount) {
      if (option === 'existing') {
        this.importerAccount.secrets = undefined
        this.importerAccount.credentials_id = ''
      } else {
        this.importerAccount.credentials_id = undefined
        this.importerAccount.secrets = {}
      }
    }
  }

  onGrpExistingCredentialSelect(accountId: string) {
    this.selectedGrpCredentialsId = accountId
    const selectedAccount = this.existingGrpAccounts.find(a => a.id === accountId)
    if (this.importerAccount && selectedAccount) {
      this.importerAccount.credentials_id = selectedAccount.credentials_id
    }
  }

  isUsingExistingGrpCredentials(): boolean {
    return this.supplierSystem?.supplier_system_auth_id === 'grp' &&
      this.existingGrpAccounts.length > 0 &&
      this.grpCredentialOption === 'existing'
  }

  getAvailableSupplierSystem(customerId: string) {
    this.customersService.getSupplierSystems(customerId).pipe(take(1)).subscribe((res: any) => {
      this.availableSystemInfoList = res
    })
  }

  addImporter(tplContent: TemplateRef<{}>, tplFooter: TemplateRef<{}>) {
    const modal: NzModalRef = this.modal.create({
      nzTitle: 'Add Datasource configuration',
      nzContent: tplContent,
      nzFooter: tplFooter,
      nzData: this.customer,
    });
  }

  buildImporterAccountPayload(): ImporterAccount {
    const payload = { ...this.importerAccount! }
    if (!payload.start_time) {
      delete payload.start_time
    }
    if (payload.max_duration_hours == null) {
      delete payload.max_duration_hours
    }
    if (!payload.website) {
      delete payload.website
    }
    if (!payload.note) {
      delete payload.note
    }
    if (this.isUsingExistingGrpCredentials()) {
      delete payload.display_name
    }
    if (payload.settings) {
      payload.settings = this.buildSettingsPayload(this.createSettingsFields, payload.settings)
    }
    return payload
  }

  buildSettingsFields(settingsSchema: any): SettingsField[] {
    const properties = settingsSchema?.properties ?? {}
    const required: string[] = settingsSchema?.required ?? []
    return Object.keys(properties).map(key =>
      this.describeSettingsField(key, properties[key], required.includes(key))
    )
  }

  private describeSettingsField(key: string, property: any, required: boolean): SettingsField {
    const type = property?.type
    let control: SettingsControl
    if (type === 'boolean') {
      control = 'boolean'
    } else if (type === 'integer' || type === 'number') {
      control = 'number'
    } else if (type === 'array' || type === 'list') {
      control = 'list'
    } else if (type === 'object') {
      control = 'object'
    } else if (Array.isArray(property?.enum)) {
      control = 'enum'
    } else if (property?.format === 'date') {
      control = 'date'
    } else {
      control = 'text'
    }

    const childProperties = property?.properties ?? {}
    const childRequired: string[] = property?.required ?? []

    return {
      key,
      label: this.humanizeSettingsKey(key),
      control,
      required,
      superUserOnly: control === 'date',
      enumOptions: control === 'enum' ? property.enum : [],
      children: control === 'object'
        ? Object.keys(childProperties).map(childKey =>
            this.describeSettingsField(childKey, childProperties[childKey], childRequired.includes(childKey)))
        : [],
    }
  }

  private humanizeSettingsKey(key: string): string {
    const spaced = key.replace(/[_-]+/g, ' ').trim()
    return spaced.charAt(0).toUpperCase() + spaced.slice(1)
  }

  initSettingsValues(fields: SettingsField[], settings: Record<string, any>): void {
    for (const field of fields) {
      const current = settings[field.key]
      switch (field.control) {
        case 'boolean':
          settings[field.key] = typeof current === 'boolean' ? current : false
          break
        case 'list':
          settings[field.key] = this.normalizeListValue(current)
          break
        case 'object': {
          const value = (current && typeof current === 'object' && !Array.isArray(current)) ? current : {}
          this.initSettingsValues(field.children, value)
          settings[field.key] = value
          break
        }
        default:
          if (current == null) {
            settings[field.key] = ''
          }
      }
    }
  }

  private normalizeListValue(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value.map(item => String(item))
    }
    if (typeof value === 'string') {
      return value.split(',').map(item => item.trim()).filter(item => item.length > 0)
    }
    return []
  }

  buildSettingsPayload(fields: SettingsField[], settings: Record<string, any> | undefined): Record<string, any> {
    const values = settings ?? {}
    const result: Record<string, any> = {}
    for (const field of fields) {
      const value = values[field.key]
      switch (field.control) {
        case 'boolean':
          result[field.key] = !!value
          break
        case 'list': {
          const list = this.normalizeListValue(value)
          if (list.length > 0 || field.required) {
            result[field.key] = list
          }
          break
        }
        case 'number':
          if (value != null && value !== '') {
            result[field.key] = Number(value)
          } else if (field.required) {
            result[field.key] = value
          }
          break
        case 'object': {
          const nested = this.buildSettingsPayload(field.children, value)
          if (Object.keys(nested).length > 0 || field.required) {
            result[field.key] = nested
          }
          break
        }
        default:
          if (value != null && String(value).trim() !== '') {
            result[field.key] = value
          } else if (field.required) {
            result[field.key] = value
          }
      }
    }
    return result
  }

  areFieldsValid(fields: SettingsField[], settings: Record<string, any> | undefined): boolean {
    const values = settings ?? {}
    return fields.every(field => this.isSettingsFieldValid(field, values[field.key]))
  }

  private isSettingsFieldValid(field: SettingsField, value: any): boolean {
    switch (field.control) {
      case 'object':
        if (!field.required) {
          return true
        }
        return field.children.every(child => this.isSettingsFieldValid(child, (value ?? {})[child.key]))
      case 'list':
        if (!field.required) {
          return true
        }
        return Array.isArray(value) && value.length > 0
      case 'boolean':
        return true
      default:
        if (!field.required) {
          return true
        }
        return value != null && value !== ''
    }
  }

  newCredentials(ref: NzModalRef) {
    this.accountCRUD = true
    this.error = undefined
    if(this.importerAccount) {
      this.customersService.addImporterAccounts(this.customer.pk, this.buildImporterAccountPayload()).pipe(take(1)).subscribe(
        {
          next: (res: any) => {
            this.accountCRUD = false
            this.getAvailableSupplierSystem(this.customer.pk)
            this.getImporterAccounts(this.customer.pk)
            this.supplierSystem = undefined
            if (res?.id) {
              this.verificationKeys[res.id] = Date.now()
              this.customersService.verifyCredentials(this.customer.pk, res.id).subscribe()
            }
            ref.close()
            this.notification.success(
              'Datasource added',
              'Importer account has been successfully created.'
            )
          },
          error: (error: any) => {
            this.error = error.error
            this.accountCRUD = false
            this.notification.error(
              'Error:',
              error.error.message,
            )
          },
          complete: () => {
            this.accountCRUD = false
          }
        }
      )
    }
  }

  areCreateSettingsValid(): boolean {
    return this.areFieldsValid(this.createSettingsFields, this.importerAccount?.settings)
  }

  areEditSettingsValid(): boolean {
    return this.areFieldsValid(this.editSettingsFields, this.editingAccount?.settings)
  }

  isNoteValid(note: string | undefined | null): boolean {
    return (note?.length ?? 0) <= 255
  }

  isCreateDisabled(): boolean {
    let disabled = true
    const displayNameValid = this.isUsingExistingGrpCredentials() || !!this.importerAccount?.display_name?.trim()
    if (this.supplierSystem && this.importerAccount?.supplier_partner_id && displayNameValid
      && this.isNoteValid(this.importerAccount?.note) && this.areCreateSettingsValid()) {
      if (this.isUsingExistingGrpCredentials()) {
        disabled = !this.selectedGrpCredentialsId
      } else {
        const schema: string[] = this.supplierSystem.secret_schema ?? []
        const secrets = this.importerAccount?.secrets ?? {}
        disabled = schema.some(field => !secrets[field] || !String(secrets[field]).trim())
      }
    }
    return disabled
  }

  closeModal(ref: NzModalRef) {
    this.supplierSystem = undefined
    this.error = undefined
    this.importerAccount = undefined
    this.existingGrpAccounts = []
    this.grpCredentialOption = 'new'
    this.selectedGrpCredentialsId = undefined
    ref.close()
  }

  openEditModal(tplContent: TemplateRef<{}>, tplFooter: TemplateRef<{}>, item: ImporterAccount) {
    this.editingAccount = {
      ...item,
      settings: { ...(item.settings ?? {}) },
    }
    this.error = undefined

    this.editingSupplierSystem = this.availableSystemInfoList.find(
      s => s.supplier_system_partner_id === item.supplier_system_partner_id
    )

    this.editSettingsFields = this.buildSettingsFields(this.editingSupplierSystem?.settings_schema)
    this.initSettingsValues(this.editSettingsFields, this.editingAccount.settings!)

    const modal: NzModalRef = this.modal.create({
      nzTitle: 'Configure importer account',
      nzContent: tplContent,
      nzFooter: tplFooter,
      nzData: item,
    })
    modal.afterClose.subscribe(() => {
      this.editingAccount = undefined
      this.editingSupplierSystem = undefined
      this.error = undefined
    })
  }

  onEditGrpOptionChange(option: 'existing' | 'new') {
    this.editGrpCredentialOption = option
    this.editSelectedGrpCredentialsId = undefined
  }

  onEditGrpExistingCredentialSelect(accountId: string) {
    this.editSelectedGrpCredentialsId = accountId
  }

  saveCredentials(ref: NzModalRef) {
    if (!this.editingAccount) return
    this.accountCRUD = true
    this.error = undefined

    let payload: any = {
      display_name: this.editingAccount.display_name,
      website: this.editingAccount.website ?? '',
      note: this.editingAccount.note ?? '',
      settings: this.buildSettingsPayload(this.editSettingsFields, this.editingAccount.settings),
    }
    if (this.editingAccount.start_time) {
      payload.start_time = this.editingAccount.start_time
    }
    if (this.editingAccount.max_duration_hours != null) {
      payload.max_duration_hours = this.editingAccount.max_duration_hours
    }

    this.customersService.updateImporterAccounts(this.customer.pk, this.editingAccount.id, payload).pipe(take(1)).subscribe({
      next: () => {
        this.accountCRUD = false
        this.getImporterAccounts(this.customer.pk)
        ref.close()
        this.notification.success('Account updated', 'Importer account has been successfully updated.')
        this.editingAccount = undefined
      },
      error: (err: any) => {
        this.error = err.error.message
        this.accountCRUD = false
        this.notification.error('Error:', err.error.message)
      },
      complete: () => { this.accountCRUD = false }
    })
  }

  isSaveDisabled(): boolean {
    if (!this.editingAccount) return true
    return !this.editingAccount.display_name?.trim() || !this.isNoteValid(this.editingAccount.note) || !this.areEditSettingsValid()
  }

  closeEditModal(ref: NzModalRef) {
    this.editingAccount = undefined
    this.editingSupplierSystem = undefined
    this.error = undefined
    ref.close()
  }

  getImporterAccounts(custId: string) {
    this.accLoading = true;
    this.customersService.getImporterAccounts(custId).pipe(
      take(1),
      switchMap((accounts: any[]) => {
        this.accounts = accounts;
        return this.customersService.getCustomerLocations(this.customer.pk).pipe(
          take(1),
          map((locations: any[]) => ({ accounts, locations }))
        );
      }),
      map(({ accounts, locations }) => {
        this.locations = locations;
        return accounts.map(account => ({
          ...account,
          partner_name: locations.find(
            (location: { partner_id: any }) =>
              location.partner_id === account.supplier_partner_id
          )?.partner_name ?? null
        }));
      })
    ).subscribe({
      next: (accountsWithPartner) => {
        this.accounts = accountsWithPartner;
        this.accLoading = false;
      },
      error: (error) => {
        this.accLoading = false;
        this.notification.error(
          'Error',
          error.error.message,
        )
      }
    });
  }

  getConfigurationRequests(id: string) {
    this.configurationRequestsLoading = true;
    this.customersService.getConfigurationRequests(id).pipe(take(1)).subscribe({
        next: (res: any) => {
          this.configurationRequestsLoading = false;
          this.configurationRequests = res;
        },
        error: (error: any) => {
          console.error(error)
          this.configurationRequestsLoading = false;
          this.notification.error(
            'Error',
            error.error.message,
          )
        }
      }
    )
  }

  verifyCredentials(importerAccountId: string) {
    this.customersService.verifyCredentials(this.customer.pk, importerAccountId).subscribe()
  }

  rotateCredentials(importerAccount: ImporterAccount) {
    const customerId = Buffer.from(this.customer.pk).toString('base64')
    const accountId = Buffer.from(importerAccount.id).toString('base64')
    this.rotatingAccountId = importerAccount.id
    this.customersService.rotateImporterCredentials(this.customer.pk, importerAccount.id).pipe(take(1)).subscribe({
      next: () => {
        this.verificationKeys[importerAccount.id] = Date.now()
        this.customersService.credentialsVerificationStatus(customerId, accountId).pipe(take(1)).subscribe()
        this.rotatingAccountId = undefined
        this.notification.success('Credentials rotated', 'Importer credentials have been rotated.')
      },
      error: (err: any) => {
        this.verificationKeys[importerAccount.id] = Date.now()
        this.customersService.credentialsVerificationStatus(customerId, accountId).pipe(take(1)).subscribe()
        this.rotatingAccountId = undefined
        this.notification.error('Error:', err.error.message)
      }
    })
  }

  onRotateChange(importerAccount: ImporterAccount, newValue: boolean): void {
    const previousValue = importerAccount.rotate;
    importerAccount.rotate = newValue;

    this.updatingRotateAccountId = importerAccount.id;
    const payload: any = {
      rotate: newValue
    };

    this.customersService.updateImporterAccounts(this.customer.pk, importerAccount.id, payload).pipe(take(1)).subscribe({
      next: () => {
        this.updatingRotateAccountId = undefined;
        this.notification.success(
          'Rotate updated',
          'Rotate setting has been successfully updated.'
        );
      },
      error: (err: any) => {
        this.updatingRotateAccountId = undefined;
        importerAccount.rotate = previousValue;
        this.notification.error(
          'Error:',
          err.error.message
        );
      }
    });
  }

  onEnabledChange(importerAccount: ImporterAccount, newValue: boolean): void {
    const previousValue = importerAccount.enabled;
    importerAccount.enabled = newValue;

    this.updatingEnabledAccountId = importerAccount.id;
    const payload: any = {
      enabled: newValue
    };

    this.customersService.updateImporterAccounts(this.customer.pk, importerAccount.id, payload).pipe(take(1)).subscribe({
      next: () => {
        this.updatingEnabledAccountId = undefined;
        this.notification.success(
          'Enabled updated',
          'Enabled setting has been successfully updated.'
        );
      },
      error: (err: any) => {
        this.updatingEnabledAccountId = undefined;
        importerAccount.enabled = previousValue;
        this.notification.error(
          'Error:',
          err.error.message
        );
      }
    });
  }

  openCredentialEditModal(tplContent: TemplateRef<{}>, tplFooter: TemplateRef<{}>, item: ImporterAccount) {
    this.editingCredentialsAccount = { ...item }
    this.editingCredentialsData = {}
    this.editingCredentialsGrpOption = 'new'
    this.editingCredentialsSelectedGrpId = undefined
    this.error = undefined

    this.editingCredentialsSupplierSystem = this.availableSystemInfoList.find(
      s => s.supplier_system_partner_id === item.supplier_system_partner_id
    )

    if (this.editingCredentialsSupplierSystem?.supplier_system_auth_id === 'grp') {
      this.editingCredentialsExistingGrpAccounts = this.accounts.filter(a =>
          a.id !== item.id && this.availableSystemInfoList.find(
            s => s.supplier_system_partner_id === a.supplier_system_partner_id
          )?.supplier_system_auth_id === 'grp'
      )
    } else {
      this.editingCredentialsExistingGrpAccounts = []
    }

    const modal: NzModalRef = this.modal.create({
      nzTitle: 'Edit credentials',
      nzContent: tplContent,
      nzFooter: tplFooter,
      nzData: item,
    })
    modal.afterClose.subscribe(() => {
      this.editingCredentialsAccount = undefined
      this.editingCredentialsSupplierSystem = undefined
      this.editingCredentialsExistingGrpAccounts = []
      this.editingCredentialsGrpOption = 'new'
      this.editingCredentialsSelectedGrpId = undefined
      this.editingCredentialsData = {}
      this.error = undefined
    })
  }

  onCredentialsGrpOptionChange(option: 'existing' | 'new') {
    this.editingCredentialsGrpOption = option
    this.editingCredentialsSelectedGrpId = undefined
  }

  onCredentialsGrpExistingSelect(accountId: string) {
    this.editingCredentialsSelectedGrpId = accountId
  }

  saveCredentialsEdit(ref: NzModalRef) {
    if (!this.editingCredentialsAccount || !this.editingCredentialsSupplierSystem) return
    this.accountCRUD = true
    this.error = undefined

    const isGrp = this.editingCredentialsSupplierSystem.supplier_system_auth_id === 'grp'
    const useExisting = isGrp && this.editingCredentialsGrpOption === 'existing'

    let payload: any = {}
    if (useExisting) {
      const sourceAccount = this.editingCredentialsExistingGrpAccounts.find(a => a.id === this.editingCredentialsSelectedGrpId)
      payload.link_to_new_credentials_id = sourceAccount?.credentials_id
    } else {
      const secrets: Record<string, string> = {}
      for (const field of this.editingCredentialsSupplierSystem.secret_schema ?? []) {
        secrets[field] = this.editingCredentialsData[field]
      }
      payload.secrets = secrets
    }

    this.customersService.updateImporterAccounts(this.customer.pk, this.editingCredentialsAccount.id, payload).pipe(take(1)).subscribe({
      next: () => {
        this.accountCRUD = false
        this.verificationKeys[this.editingCredentialsAccount!.id] = Date.now()
        this.customersService.verifyCredentials(this.customer.pk, this.editingCredentialsAccount!.id).subscribe()
        this.getImporterAccounts(this.customer.pk)
        ref.close()
        this.notification.success('Credentials updated', 'Importer credentials have been successfully updated.')
        this.editingCredentialsAccount = undefined
      },
      error: (err: any) => {
        this.error = err.error.message
        this.accountCRUD = false
        this.notification.error('Error:', err.error.message)
      },
      complete: () => { this.accountCRUD = false }
    })
  }

  isCredentialsSaveDisabled(): boolean {
    if (!this.editingCredentialsAccount || !this.editingCredentialsSupplierSystem) return true

    const isGrp = this.editingCredentialsSupplierSystem.supplier_system_auth_id === 'grp'

    if (isGrp && this.editingCredentialsExistingGrpAccounts.length > 0 && this.editingCredentialsGrpOption === 'existing') {
      return !this.editingCredentialsSelectedGrpId
    }

    const schema: string[] = this.editingCredentialsSupplierSystem.secret_schema ?? []
    return schema.some(field => !this.editingCredentialsData[field])
  }

  closeCredentialEditModal(ref: NzModalRef) {
    this.editingCredentialsAccount = undefined
    this.editingCredentialsSupplierSystem = undefined
    this.editingCredentialsExistingGrpAccounts = []
    this.editingCredentialsGrpOption = 'new'
    this.editingCredentialsSelectedGrpId = undefined
    this.editingCredentialsData = {}
    this.error = undefined
    ref.close()
  }


  deleteAccount(tplContent: TemplateRef<{}>, tplFooter: TemplateRef<{}>, item: any) {
    const modal: NzModalRef = this.modal.create({
      nzTitle: 'Delete importer account',
      nzContent: tplContent,
      nzFooter: tplFooter,
      nzData: item,
    });
    modal.afterClose.subscribe(() => {
      this.deleteTextValue = ''
      this.error = undefined
    });
  }


  isDeleteConfirmed(ref: NzModalRef): boolean {
    const expected = ('delete ' + ref.getConfig().nzData.supplier_system_partner_id).toLowerCase()
    return this.deleteTextValue.trim().toLowerCase() === expected
  }

  isVerificationNotVerified(verificationStatus: string): boolean {
    return verificationStatus !== 'verified'
  }

  delete(ref: NzModalRef) {
    this.accountCRUD = true
    let importerAccountId = ref.getConfig().nzData.id
    this.customersService.deleteAccount(this.customer.pk, importerAccountId).pipe(take(1)).subscribe({
      next: (res: any) => {
        this.accountCRUD = false
        this.getAvailableSupplierSystem(this.customer.pk)
        this.getImporterAccounts(this.customer.pk)
        ref.close()
        this.notification.success(
          'Account deleted',
          'Importer account has been successfully removed.'
        )
      },
      error: (err: any) => {
        this.error = err.error
        this.accountCRUD = false
        this.notification.error(
          'Error',
          err.error.message,
        )
      },
      complete: () => {
        this.accountCRUD = false
      }
    })
  }
}
